import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { parseJson } from "@crescendolab/parse-json";
import { WHATSAPP_CONFIG_ID } from "@polifonia/env";
import { useStatsigClient } from "@statsig/react-bindings";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { logError } from "@/features/logger";
import { fbSdk } from "@/lib/fbSdk";

const CustomerInfoSchema = z.object({
  waba_id: z.string(),
  phone_number_id: z.string(),
});

const BusinessInfoSchema = z.object({
  waba_id: z.string(),
  business_id: z.string(),
});

type CustomerInfo = z.infer<typeof CustomerInfoSchema>;

const EmbeddedSignupBaseSchema = z.object({
  type: z.literal("WA_EMBEDDED_SIGNUP"),
});

const EmbeddedSignupSchema = z.discriminatedUnion("event", [
  EmbeddedSignupBaseSchema.extend({
    event: z.enum(["FINISH", "FINISH_ONLY_WABA"]),
    data: CustomerInfoSchema,
  }),
  EmbeddedSignupBaseSchema.extend({
    event: z.literal("FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"),
    data: BusinessInfoSchema,
  }),
  EmbeddedSignupBaseSchema.extend({
    event: z.literal("CANCEL"),
    data: z.object({
      current_step: z.string(),
    }),
  }),
]);

type UseWhatsappLoginOptions = {
  onError?: (error?: Error) => void;
  onSuccess?: ({
    wabaId,
    phoneNumberId,
    signupAuthCode,
  }: {
    wabaId: string;
    phoneNumberId: string;
    signupAuthCode: string;
  }) => void;
};

export function useWhatsappLogin(options?: UseWhatsappLoginOptions) {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>();
  const statsigClient = useStatsigClient();
  const enabledCoexistenceWhatsapp = statsigClient.checkGate(
    "whatsapp_embedded_signup_coexistence",
  );

  const handleError = useCallback(
    (error?: Error) => {
      options?.onError?.(error);
    },
    [options],
  );

  const loginMutation = fbSdk.hooks.mutations.useLoginMutation({
    onSuccess: async (data) => {
      if (
        data.status !== "connected" ||
        !data.authResponse?.code ||
        !customerInfo
      ) {
        handleError();
        return;
      }

      options?.onSuccess?.({
        wabaId: customerInfo.waba_id,
        phoneNumberId: customerInfo.phone_number_id,
        signupAuthCode: data.authResponse.code,
      });
    },
    onError: handleError,
  });

  // https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation/#session-logging-message-event-listener
  useEffect(() => {
    const handleWhatsappEmbeddedSignupMessage = (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      const parsedData = parseJson(event.data, { fallback: null });

      if (!parsedData) {
        return;
      }

      const result = EmbeddedSignupSchema.safeParse(parsedData);

      if (!result.success) {
        handleError(result.error);
        logError(result.error);
        return;
      }

      if (result.data.event === "CANCEL") return;

      const eventType = result.data.event;
      switch (eventType) {
        case "FINISH":
        case "FINISH_ONLY_WABA":
          setCustomerInfo({
            waba_id: result.data.data.waba_id,
            phone_number_id: result.data.data.phone_number_id,
          });
          break;
        case "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING":
          setCustomerInfo({
            waba_id: result.data.data.waba_id,
            phone_number_id: "",
          });
          break;
        default:
          eventType satisfies never;
          throw new Error(
            inspectMessage`Unhandled event type: ${eventType} with data: ${result.data}`,
          );
      }
    };

    window.addEventListener("message", handleWhatsappEmbeddedSignupMessage);
    return () =>
      window.removeEventListener(
        "message",
        handleWhatsappEmbeddedSignupMessage,
      );
  }, [handleError]);

  const startLogin = ({
    extras,
  }: {
    extras?: facebook.LoginOptions["extras"];
  } = {}) => {
    loginMutation.mutate({
      loginOptions: {
        config_id: WHATSAPP_CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: !enabledCoexistenceWhatsapp
            ? ""
            : "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
          ...extras,
        },
      },
    });
  };

  return {
    startLogin,
    isPending: loginMutation.isPending,
  };
}
